import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import api from "@/lib/api";

const DynamicContentBlocks = ({ page, position }) => {
  const [blocks, setBlocks] = useState([]);
  
  useEffect(() => {
    const loadBlocks = async () => {
      try {
        const params = new URLSearchParams();
        if (page) params.append("page", page);
        if (position) params.append("position", position);
        
        const res = await api.get(`/content-blocks?${params.toString()}`);
        setBlocks(res.data || []);
      } catch (err) {
        console.log("No content blocks");
      }
    };
    loadBlocks();
  }, [page, position]);
  
  if (blocks.length === 0) return null;
  
  const getButtonClasses = (style) => {
    switch (style) {
      case 'whatsapp':
        return 'bg-[#25D366] hover:bg-[#20bd5a] text-white';
      case 'secondary':
        return 'bg-[#556B2F] hover:bg-[#4a5d29] text-white';
      case 'outline':
        return 'bg-transparent border-2 border-current hover:bg-black/5';
      default: // primary
        return 'bg-[#8B4513] hover:bg-[#6d360f] text-white';
    }
  };
  
  const renderButton = (block) => {
    if (!block.button_text || !block.button_url) return null;
    
    const isExternal = block.button_url.startsWith('http') || block.button_url.startsWith('https');
    const isWhatsApp = block.button_url.includes('whatsapp') || block.button_url.includes('wa.me');
    const isFile = block.button_url.startsWith('/api/files') || block.button_url.startsWith('/api/uploads');
    
    const buttonClasses = `inline-flex items-center gap-2 px-6 py-3 rounded-full font-medium transition-all ${getButtonClasses(block.button_style)}`;
    
    if (isExternal || isWhatsApp || isFile) {
      const href = isFile ? `${process.env.REACT_APP_BACKEND_URL}${block.button_url}` : block.button_url;
      return (
        <a 
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className={buttonClasses}
        >
          {block.button_text}
          <ExternalLink className="w-4 h-4" />
        </a>
      );
    }
    
    return (
      <Link to={block.button_url} className={buttonClasses}>
        {block.button_text}
      </Link>
    );
  };
  
  return (
    <div className="space-y-4">
      {blocks.map((block) => (
        <section 
          key={block.id}
          className="py-8 md:py-12"
          style={{ 
            backgroundColor: block.background_color || '#F5F5DC',
            color: block.text_color || '#2C2C2C'
          }}
          data-testid={`content-block-${block.id}`}
        >
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className={`flex flex-col ${block.image_url ? 'md:flex-row md:items-center gap-8' : 'items-center text-center'}`}>
              
              {/* Image */}
              {block.image_url && (
                <div className="md:w-1/3 flex-shrink-0">
                  <img loading="lazy" decoding="async" 
                    src={block.image_url}
                    alt={block.title}
                    className="w-full h-48 md:h-64 object-cover rounded-xl"
                  />
                </div>
              )}
              
              {/* Content */}
              <div className={`flex-1 ${block.image_url ? '' : 'max-w-3xl'}`}>
                <h2 
                  className="text-2xl md:text-3xl font-bold mb-4"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  {block.title}
                </h2>
                
                {block.content && (
                  <p className="text-base md:text-lg opacity-90 mb-6 whitespace-pre-line">
                    {block.content}
                  </p>
                )}
                
                {renderButton(block)}
              </div>
            </div>
          </div>
        </section>
      ))}
    </div>
  );
};

export default DynamicContentBlocks;
